/**
 * Derivazione "Da notificare" (Slice D — worklist clienti).
 *
 * Una coppia (vulnId, customerId) e' "da notificare" se il cliente e' impattato
 * dalla release (consegna corrente sulla release) ma il ledger non contiene
 * ancora una riga con status='sent' per quella coppia. Pura derivazione lato
 * client: nessuna chiamata API aggiuntiva, nessun nuovo flow.
 */

import type { CustomerVulnNotification, ImpactedCustomer } from './types.js';

/** Chiave univoca per una coppia (vulnId, customerId) nel ledger. */
function chiave(vulnId: string, customerId: number): string {
  return `${vulnId}::${customerId}`;
}

/** Insieme delle coppie (vulnId, customerId) che hanno gia' un invio 'sent'. */
function coppieGiaInviate(notifiche: CustomerVulnNotification[]): Set<string> {
  return new Set(
    notifiche.filter((n) => n.status === 'sent').map((n) => chiave(n.vulnId, n.customerId)),
  );
}

/**
 * Per ogni finding (vulnId), i clienti impattati che NON hanno ancora un invio
 * 'sent' nel ledger per quella coppia (vulnId, customerId).
 */
export function clientiDaNotificarePerFinding(
  vulnIds: string[],
  clientiImpattati: ImpactedCustomer[],
  notifiche: CustomerVulnNotification[],
): Map<string, ImpactedCustomer[]> {
  const inviate = coppieGiaInviate(notifiche);
  const risultato = new Map<string, ImpactedCustomer[]>();

  for (const vulnId of vulnIds) {
    const daNotificare = clientiImpattati.filter((c) => !inviate.has(chiave(vulnId, c.customerId)));
    risultato.set(vulnId, daNotificare);
  }

  return risultato;
}

/** Numero totale di coppie (finding, cliente) ancora da notificare. */
export function contaTotaleDaNotificare(perFinding: Map<string, ImpactedCustomer[]>): number {
  let totale = 0;
  for (const clienti of perFinding.values()) {
    totale += clienti.length;
  }
  return totale;
}
